import type { ReactNode } from "react";
import {
  AlertTriangle,
  ArrowLeftRight,
  BookOpen,
  CheckCircle2,
  Compass,
  MessageSquare,
  Minus,
} from "lucide-react";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import {
  FitCulturelMatchingChart,
  RapportsMenu,
  type RapportPropose,
  type LigneMatchingFitCulturel,
} from "@repo/results";
import { labelCategorieFitCulturel, dateCourte } from "@repo/utils";
import type {
  Convergence,
  Friction,
  IndicateurGlobal,
  LigneDimension,
  RapportMatchingCulturel as Rapport,
  SensVerdict,
} from "../../../../convex/referentiel/rapport";
import { rCritique } from "../../../../convex/referentiel/rapport";

/**
 * Rapport de matching culturel d'un candidat (fit-culturel, échelle Q-sort).
 *
 * Suit l'ordre de lecture du modèle métier — et cet ordre est le message : les
 * PÔLES du tri (§2) sont psychométriquement les positions les plus fiables, ils
 * viennent donc avant le détail par dimension. L'indicateur global (§1) ouvre le
 * rapport, en mots d'abord (« Proche », « Pas de tendance nette »…), et porte sa
 * propre mise en garde : sans tendance nette il n'y a rien à lire, et le rapport
 * le dit au lieu d'afficher un demi-score.
 *
 * Tout le calcul vient de `convex/referentiel/rapport.ts` (module pur) : ce
 * composant ne fait que mettre en forme, aucun seuil n'est redéfini ici.
 */

type Props = {
  rapport: Rapport;
  candidat: string;
  /** Nom de la campagne, tenant lieu de poste visé. */
  campagneNom?: string;
  datePassation?: number;
  /** Identité de la version du référentiel qui a produit ce rapport. */
  reference: { version: number; computedAt: number; nbReferents: number };
  /** Rang intra-campagne, pour situer le candidat. */
  rang?: { position: number; total: number };
  /**
   * De quoi proposer les rapports (PDF ou Word) de la passation — dont celui REMIS AU
   * CANDIDAT, écrit à la deuxième personne, et donc différent de cet écran, qui
   * parle du candidat au recruteur. `rapports` vient de
   * `api.reports.rapportsDisponibles`.
   *
   * Optionnel : l'écran doit rester affichable sans lui (tests, aperçus), et le
   * jeton comme l'URL du déploiement viennent de l'app appelante — ce paquet ne
   * lit aucun `process.env` (il serait vide hors de son bundle).
   */
  rapportPdf?: {
    evaluationId: string;
    prenom?: string;
    nom?: string;
    rapports: RapportPropose[] | undefined;
    token: string | null;
    convexUrl: string;
  };
};

const arrondi1 = (n: number) => (Math.round(n * 10) / 10).toLocaleString("fr-FR");
const arrondi2 = (n: number) =>
  (Math.round(n * 100) / 100).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
  });

/**
 * r signé à deux décimales (« +0,42 », « −0,45 ») : le signe EST le sens du
 * matching, il doit se lire sans le deviner. Même format au classement, à la
 * synthèse et dans ce rapport.
 */
export function formatR(r: number): string {
  const arrondi = Math.round(r * 100) / 100;
  const texte = Math.abs(arrondi).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return arrondi > 0 ? `+${texte}` : arrondi < 0 ? `−${texte}` : texte;
}

/** p très petit : « 0,00 » se lirait comme une absence de valeur. */
const formatP = (p: number) => (p < 0.001 ? "< 0,001" : arrondi2(p));

const labelCategorie = (cle: string | null) =>
  cle === null ? "Sans catégorie" : (labelCategorieFitCulturel(cle) ?? cle);

// Même axe diverging que l'onglet Matching : proche et éloigné se lisent comme
// deux pôles, « pas de tendance nette » comme « rien ». Jamais la couleur seule
// — chaque verdict porte aussi son libellé (`IndicateurGlobal.libelle`).
export const COULEUR_VERDICT: Record<SensVerdict, string> = {
  proche: "var(--chart-region-4)",
  eloigne: "var(--chart-region-2)",
  neutre: "var(--muted-foreground)",
};

/** Badge du verdict : plein pour un verdict net, contour pour « à la limite »
 *  et pour l'absence de tendance — la forme dit la force, pas seulement le mot. */
function varianteBadge(
  indicateur: IndicateurGlobal,
): "success" | "warning" | "outline" {
  if (indicateur.sens === "neutre" || indicateur.aLaLimite) return "outline";
  return indicateur.sens === "proche" ? "success" : "warning";
}

function ChampIdentification({
  libelle,
  children,
}: {
  libelle: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted-foreground text-xs">{libelle}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

/**
 * Curseur de r sur l'axe [-1, 1], avec la bande « pas de tendance nette »
 * grisée au centre.
 *
 * La bande n'a PAS de largeur fixe : elle dépend de n (à 23 items il faut
 * |r| ≈ 0,41 pour sortir du bruit, à 15 items ≈ 0,51). Elle est donc tracée
 * depuis `rCritique(n)` plutôt que sur un seuil dessiné en dur qui mentirait sur
 * les petits questionnaires.
 */
function AxeCorrelation({
  r,
  n,
  sens,
}: {
  r: number;
  n: number;
  sens: SensVerdict;
}) {
  const position = ((r + 1) / 2) * 100;
  const demiBande = rCritique(n) * 50;
  return (
    <div className="space-y-1">
      <div className="relative h-2 w-full rounded-full bg-muted">
        <div
          className="absolute inset-y-0 bg-foreground/10"
          style={{ left: `${50 - demiBande}%`, width: `${2 * demiBande}%` }}
        />
        <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-foreground/30" />
        <div
          className="absolute -top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-background"
          style={{
            left: `${position}%`,
            backgroundColor: COULEUR_VERDICT[sens],
          }}
        />
      </div>
      <div className="text-muted-foreground flex justify-between text-xs">
        <span>−1 · éloigné</span>
        <span>pas de tendance nette</span>
        <span>+1 · proche</span>
      </div>
    </div>
  );
}

function BlocPoles({
  titre,
  description,
  icone,
  vide,
  children,
  compte,
}: {
  titre: string;
  description: string;
  icone: ReactNode;
  vide: string;
  compte: number;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {icone}
          {titre}
          <span className="text-muted-foreground text-sm font-normal">
            ({compte})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground text-xs">{description}</p>
        {compte === 0 ? (
          <p className="text-muted-foreground">{vide}</p>
        ) : (
          <ul className="space-y-2">{children}</ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Un pôle peu consensuel entre référents est une base fragile — on le dit. */
const SEUIL_DISPERSION_FRAGILE = 1;

function LigneItem({
  label,
  categorie,
  detail,
  dispersion,
}: {
  label: string;
  categorie?: string;
  detail: string;
  dispersion: number;
}) {
  return (
    <li className="space-y-0.5">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-medium">{label}</span>
        {categorie && (
          <span className="text-muted-foreground text-xs">
            {labelCategorie(categorie)}
          </span>
        )}
        {dispersion >= SEUIL_DISPERSION_FRAGILE && (
          <Badge variant="outline">référents dispersés</Badge>
        )}
      </div>
      <p className="text-muted-foreground text-xs">{detail}</p>
    </li>
  );
}

const detailConvergence = (c: Convergence) =>
  c.pole === "haut"
    ? "Valorisé par le candidat et caractéristique de l'entreprise."
    : "Écarté par le candidat comme par l'entreprise.";

const detailFriction = (f: Friction) =>
  f.poleCandidat === "haut"
    ? "Priorité haute du candidat, que l'entreprise place tout en bas."
    : "Écarté par le candidat, alors que l'entreprise en fait une priorité.";

/** Position du candidat et de l'entreprise sur l'échelle du Q-sort. */
function TrackPositions({
  ligne,
  nbNiveaux,
}: {
  ligne: LigneDimension;
  nbNiveaux: number;
}) {
  const pct = (v: number) =>
    Math.max(0, Math.min(100, ((v - 1) / (nbNiveaux - 1)) * 100));
  return (
    <div className="relative h-2 w-full min-w-24 rounded-full bg-muted">
      <div
        className="absolute inset-y-0 rounded-full bg-foreground/20"
        style={{
          left: `${Math.min(pct(ligne.positionCandidat), pct(ligne.positionEntreprise))}%`,
          width: `${Math.abs(pct(ligne.positionCandidat) - pct(ligne.positionEntreprise))}%`,
        }}
      />
      <div
        className="absolute -top-0.5 h-3 w-1 -translate-x-1/2 rounded-full bg-muted-foreground"
        style={{ left: `${pct(ligne.positionEntreprise)}%` }}
        title={`Entreprise ${arrondi1(ligne.positionEntreprise)}`}
      />
      <div
        className="absolute -top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-background bg-primary"
        style={{ left: `${pct(ligne.positionCandidat)}%` }}
        title={`Candidat ${arrondi1(ligne.positionCandidat)}`}
      />
    </div>
  );
}

export function RapportMatchingCulturel({
  rapport,
  candidat,
  rapportPdf,
  campagneNom,
  datePassation,
  reference,
  rang,
}: Props) {
  const { indicateur, extremes, parDimension, parItem, nbNiveaux } = rapport;
  const correlation = indicateur.correlation;

  // Le socle d'items n'est plus catégorisé (ses regroupements PCfit sont stockés
  // mais pas restitués) : tous les items tombent alors dans l'unique groupe « sans
  // catégorie », et le §3 se réduit à une ligne qui redit l'alignement global du
  // §1 sur les 23 items. On le masque plutôt que d'afficher un
  // tableau à une ligne intitulé « Sans catégorie ». Le §3 reste rendu pour les
  // campagnes configurées sous un socle catégorisé, où il localise les tensions.
  const detailParCategorie =
    parDimension.length > 1 || parDimension[0]?.cle !== null;

  // Numérotation dynamique : le §3 « Détail par dimension » n'existe pas sur un
  // socle non catégorisé (cf. detailParCategorie ci-dessus), donc les pistes
  // d'entretien qui le suivent glissent en §3 plutôt que de laisser un trou
  // (2 → 4) dans la numérotation affichée.
  const numeroDetailDimension = 3;
  const numeroPistesEntretien = detailParCategorie ? 4 : 3;

  // Seul le libellé de catégorie est traduit ici, le reste vient tel quel de
  // `matchingParItem`. L'ORDRE, lui, est réimposé : les items sont triés par
  // libellé, ce qui rend le découpage du cercle prévisible et permet de
  // retrouver un item d'un radar à l'autre. Tri local à l'affichage — les
  // tableaux du rapport gardent l'ordre de `matchingParItem`.
  const lignesRadar: LigneMatchingFitCulturel[] = parItem
    .map((ligne) => ({
      ...ligne,
      categorie:
        ligne.categorie === null ? null : labelCategorie(ligne.categorie),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));

  return (
    <div className="space-y-4">
      {/* ---- En-tête et identification ---- */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-3 text-base">
            <Compass className="h-4 w-4" />
            Rapport de matching culturel
            <Badge variant={varianteBadge(indicateur)}>
              {indicateur.libelle}
            </Badge>
          </CardTitle>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className="text-muted-foreground text-sm">
              Matching entre les attentes du candidat et ce que les référents
              jugent nécessaire pour réussir dans le poste et l'organisation.
            </p>
            {rapportPdf && (
              <RapportsMenu
                evaluationId={rapportPdf.evaluationId}
                rapports={rapportPdf.rapports}
                candidatInfo={{
                  prenom: rapportPdf.prenom,
                  nom: rapportPdf.nom,
                }}
                token={rapportPdf.token}
                convexUrl={rapportPdf.convexUrl}
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ChampIdentification libelle="Candidat">
              {candidat || "—"}
            </ChampIdentification>
            <ChampIdentification libelle="Poste visé">
              {campagneNom ?? "—"}
            </ChampIdentification>
            <ChampIdentification libelle="Date de passation">
              {datePassation ? dateCourte(datePassation) : "—"}
            </ChampIdentification>
            <ChampIdentification libelle="Réf. profil culture entreprise">
              v{reference.version} · {reference.nbReferents} référent
              {reference.nbReferents > 1 ? "s" : ""} ·{" "}
              {dateCourte(reference.computedAt)}
            </ChampIdentification>
          </dl>
        </CardContent>
      </Card>

      {/* ---- §1 Indicateur global ---- */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">1. Indicateur global</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {correlation === null ? (
            <p className="text-sm">{indicateur.interpretation}</p>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-6">
                <div>
                  <p className="text-muted-foreground text-xs">
                    Corrélation moyenne avec les référents (r)
                  </p>
                  <p className="text-2xl font-semibold tabular-nums">
                    {formatR(correlation.r)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">
                    Significativité (p)
                  </p>
                  <p className="text-2xl font-semibold tabular-nums">
                    {formatP(correlation.p)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">
                    Items appariés
                  </p>
                  <p className="text-2xl font-semibold tabular-nums">
                    {correlation.n}
                  </p>
                </div>
              </div>
              <AxeCorrelation
                r={correlation.r}
                n={correlation.n}
                sens={indicateur.sens}
              />
              <p className="text-sm">{indicateur.interpretation}</p>
            </>
          )}

          {rang && (
            <p className="text-muted-foreground text-xs">
              Rang {rang.position} sur {rang.total} dans cette campagne (classé
              par corrélation moyenne avec les référents, comparable entre
              candidats de la campagne uniquement).
            </p>
          )}

          {rapport.fiabilite === "faible" && (
            <div className="bg-muted/40 flex items-start gap-2 rounded-surface p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Référentiel établi sur {rapport.nbReferents} référent
                {rapport.nbReferents > 1 ? "s" : ""} : le profil culture de
                l'entreprise reste fragile. Les convergences et frictions se
                lisent comme des hypothèses d'entretien, pas comme des constats.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- Radar de matching item par item ---- */}
      {/* Non numéroté : c'est une VUE des mêmes données que les §2/§3, pas une
          section de plus dans l'ordre de lecture du modèle métier. Placé juste
          après l'indicateur global, il donne la forme d'ensemble avant que les
          pôles ne détaillent les items qui la portent. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Radar de matching ({lignesRadar.length} items)
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            L'anneau violet est le cran attendu par le référentiel : un niveau
            attendu de 3 remplit la bande de 2 à 3. Le polygone est le profil du
            candidat, {nbNiveaux} au bord du cercle = le plus valorisé. Un point
            vert tombe dans l'anneau (même cran), un point bleu est hors de
            l'anneau — au-dessus ou en dessous, l'écart se lit à la position.
            Tous les items pèsent pareil : aucune pondération n'est appliquée.
          </p>
        </CardHeader>
        <CardContent>
          <FitCulturelMatchingChart
            lignes={lignesRadar}
            nbNiveaux={nbNiveaux}
          />
        </CardContent>
      </Card>

      {/* ---- §2 Lecture par les extrêmes ---- */}
      <div className="space-y-2">
        <div>
          <h3 className="text-base font-semibold">
            2. Lecture par les extrêmes
          </h3>
          <p className="text-muted-foreground text-sm">
            Les pôles du tri sont psychométriquement les positions les plus
            fiables (test-retest ~.87 aux extrêmes contre ~.61 au global dans la
            littérature OCP) : c'est le cœur interprétatif, à exploiter avant le
            score global — {extremes.taillePole.haut} item
            {extremes.taillePole.haut > 1 ? "s" : ""} au pôle haut,{" "}
            {extremes.taillePole.bas} au pôle bas, de chaque côté.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <BlocPoles
            titre="Convergences fortes"
            description="Item placé au même pôle par le candidat et par l'entreprise."
            icone={<CheckCircle2 className="h-4 w-4" />}
            compte={extremes.convergences.length}
            vide="Aucun item au même pôle des deux côtés."
          >
            {extremes.convergences.map((c) => (
              <LigneItem
                key={c.code}
                label={c.label}
                categorie={c.categorie}
                detail={detailConvergence(c)}
                dispersion={c.dispersionEntreprise}
              />
            ))}
          </BlocPoles>
          <BlocPoles
            titre="Frictions"
            description="Item à un extrême pour le candidat, à l'extrême opposé pour l'entreprise. Une friction n'est pas éliminatoire : c'est un point à explorer en entretien."
            icone={<ArrowLeftRight className="h-4 w-4" />}
            compte={extremes.frictions.length}
            vide="Aucun item aux pôles opposés."
          >
            {extremes.frictions.map((f) => (
              <LigneItem
                key={f.code}
                label={f.label}
                categorie={f.categorie}
                detail={detailFriction(f)}
                dispersion={f.dispersionEntreprise}
              />
            ))}
          </BlocPoles>
        </div>
      </div>

      {/* ---- §3 Détail par dimension ---- */}
      {detailParCategorie && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {numeroDetailDimension}. Détail par dimension
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            Position moyenne des items de la dimension sur l'échelle du tri —{" "}
            {nbNiveaux} = le plus valorisé, 1 = le moins. Ce n'est pas un rang :
            une position haute signifie « fortement valorisé ». Un écart faible
            (|écart| ≤ 1) vaut alignement ; écart positif = le candidat valorise
            davantage, négatif = l'entreprise accentue davantage.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {parDimension.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              Aucune dimension exploitable : la passation du candidat ne recoupe
              pas le référentiel.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                      Dimension
                    </th>
                    <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                      Candidat
                    </th>
                    <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                      Entreprise
                    </th>
                    <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                      Écart
                    </th>
                    <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                      Lecture
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parDimension.map((ligne) => (
                    <tr
                      key={ligne.cle ?? "__sans__"}
                      className="border-b last:border-0"
                    >
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="font-medium">
                            {labelCategorie(ligne.cle)}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {ligne.nbItems} item
                            {ligne.nbItems > 1 ? "s" : ""}
                          </span>
                        </div>
                        <TrackPositions ligne={ligne} nbNiveaux={nbNiveaux} />
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {arrondi1(ligne.positionCandidat)}
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {arrondi1(ligne.positionEntreprise)}
                      </td>
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                        {ligne.ecart === 0
                          ? "0"
                          : `${ligne.ecart > 0 ? "+" : "−"}${arrondi1(Math.abs(ligne.ecart))}`}
                      </td>
                      <td className="text-muted-foreground px-3 py-2">
                        {ligne.lecture}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* ---- §4 Pistes d'entretien ---- */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4" />
            {numeroPistesEntretien}. Pistes d'entretien
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            À explorer avec le candidat.
          </p>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            {rapport.pistes.map((piste) => (
              <li key={piste} className="flex gap-2">
                <Minus className="mt-1 h-3 w-3 shrink-0" />
                <span>{piste}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* ---- Notice d'interprétation ---- */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BookOpen className="h-4 w-4" />
            Notice d'interprétation
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            Les règles de lecture des sections ci-dessus. Le rapport ne
            s'interprète pas sans elles.
          </p>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3 sm:grid-cols-2">
            {rapport.notice.map((entree) => (
              <div key={entree.titre} className="space-y-0.5">
                <dt className="text-sm font-medium">{entree.titre}</dt>
                <dd className="text-muted-foreground text-xs">
                  {entree.texte}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* ---- Cadre d'usage : ne circule jamais séparé du rapport ---- */}
      <p className="text-muted-foreground border-t pt-3 text-xs">
        {rapport.avertissement}
      </p>
    </div>
  );
}
